import { useEffect, useState, type ReactNode } from 'react';
import { Group, Panel, Separator, useGroupRef } from 'react-resizable-panels';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PREFIXO_API, ROTAS } from '@remoto/shared';
import { chamar } from '../api';
import { usarSessao } from '../estado/sessao';
import { usarTema, type Tema } from '../estado/tema';
import { fecharSocketDoPainel } from '../socket/painel';
import { escreverWorkspace, type EstadoDoWorkspace, type LadoAberto } from './estado';
import { gravarMenuRecolhido, lerMenuRecolhido } from './memoria';
import {
  IconeAviso,
  IconeMenu,
  IconeTemaClaro,
  IconeTemaEscuro,
  IconeTemaSistema,
} from '../componentes/icones';
import { paginasVisiveis, type IconeDePagina } from './paginas';
import { usarSessaoMestre } from '../sessoes/mestre';
import { lerMotorDaProsa, mestreDaProsa } from '../prosa/escolha';
import { IconeMicrofone } from '../componentes/icones';

/**
 * Abaixo disto o workspace de três colunas não cabe, e espremê-lo só faz três
 * painéis ruins no lugar de uma tela boa. O celular tem layout próprio.
 */
export const LARGURA_MINIMA = 1024;

export function usarTelaLarga(): boolean {
  const [larga, setLarga] = useState(
    () => typeof window === 'undefined' || window.innerWidth >= LARGURA_MINIMA,
  );
  useEffect(() => {
    const consulta = window.matchMedia(`(min-width: ${LARGURA_MINIMA}px)`);
    const aoMudar = (): void => setLarga(consulta.matches);
    aoMudar();
    consulta.addEventListener('change', aoMudar);
    return () => consulta.removeEventListener('change', aoMudar);
  }, []);
  return larga;
}

interface Eu {
  recursos: {
    transcricao: boolean;
    arquivos: boolean;
    notificacoes: boolean;
    /** A seção de Áudio no menu; separada do provedor de transcrição. */
    abaAudio?: boolean;
  };
}

interface MaquinaNaBarra {
  id: string;
  nome: string;
  estado: string;
}

/**
 * O menu lateral: as telas do painel.
 *
 * Aqui só entra o que é destino — Áudio, Máquinas, Contas, Saúde. O que troca
 * a lateral de trabalho (Sessões e Explorador) fica nas abas dela, e não neste
 * menu: são duas perguntas diferentes ("para onde vou" e "o que estou olhando
 * enquanto trabalho"), e misturá-las foi o que deixou a barra confusa.
 */
/** As três escolhas de tema, na ordem em que fazem sentido ler. */
const TEMAS: { id: Tema; Icone: IconeDePagina; titulo: string }[] = [
  { id: 'sistema', Icone: IconeTemaSistema, titulo: 'Seguir o sistema' },
  { id: 'claro', Icone: IconeTemaClaro, titulo: 'Claro' },
  { id: 'escuro', Icone: IconeTemaEscuro, titulo: 'Escuro' },
];

/** As abas da lateral de trabalho. */
/**
 * A largura de cada painel em cada arranjo, em porcentagem.
 *
 * `centro` e `direita` sao do grupo de dentro e somam 100 entre si; `lateral`
 * e do grupo de fora, e o que sobra vai para o resto.
 */
const LARGURAS = {
  // Fora do trabalho a tela ocupa tudo; lateral e conversa ficam vivas atras.
  tela: { lateral: 0, centro: 100, direita: 0 },
  // Sem arquivo aberto, a conversa fica com o lugar do editor.
  conversa: { lateral: 18, centro: 0, direita: 100 },
  editor: { lateral: 20, centro: 65, direita: 35 },
} as const;

const ABAS_DA_LATERAL: { id: LadoAberto; rotulo: string }[] = [
  { id: 'sessoes', rotulo: 'Sessões' },
  { id: 'explorador', rotulo: 'Explorador' },
];

/**
 * A moldura do painel: barra de atividades, lateral, centro e o chat à direita.
 */
export function Shell({
  estado,
  naTelaDeTrabalho,
  semArquivoAberto,
  lateral,
  centro,
  direita,
}: {
  estado: EstadoDoWorkspace;
  /** Fora da tela de trabalho, a lateral e a conversa saem da frente. */
  naTelaDeTrabalho: boolean;
  /** Sem arquivo aberto, a conversa ocupa o lugar do editor. */
  semArquivoAberto: boolean;
  lateral: ReactNode;
  centro: ReactNode;
  direita: ReactNode;
}): React.JSX.Element {
  const navegar = useNavigate();
  const usuario = usarSessao((e) => e.usuario);
  const sair = usarSessao((e) => e.sair);
  const escolhaDoTema = usarTema((e) => e.escolha);
  const definirTema = usarTema((e) => e.definir);

  const { data: eu } = useQuery({
    queryKey: ['eu'],
    queryFn: () => chamar<Eu>(ROTAS.auth.eu),
    staleTime: 5 * 60_000,
  });

  const { data: maquinas = [] } = useQuery({
    queryKey: ['maquinas-barra'],
    queryFn: () => chamar<MaquinaNaBarra[]>(ROTAS.maquinas),
    refetchInterval: 30_000,
  });

  const { data: pendentes = [] } = useQuery({
    queryKey: ['permissoes-pendentes'],
    queryFn: () => chamar<unknown[]>(`${ROTAS.permissoes}?estado=PENDENTE`),
    refetchInterval: 15_000,
  });

  /**
   * O arranjo da moldura, e a largura de cada painel em cada um.
   *
   * Os três painéis ficam **sempre montados** — a lateral e a conversa guardam
   * socket e rolagem — e o que muda é a largura, mandada de uma vez. Foram
   * duas tentativas erradas antes desta: encolher por `collapse()` funcionava
   * ao abrir a página direto e não ao navegar até ela, porque trocar o
   * conjunto de painéis fazia o grupo reinicializar por cima; e deixar o
   * tamanho para os padrões esbarrava nos tetos, que também são proporção.
   *
   * Mandar o layout inteiro tira a adivinhação: é um número por painel, e o
   * teste de tela confere os três arranjos.
   */
  const arranjo = !naTelaDeTrabalho ? 'tela' : semArquivoAberto ? 'conversa' : 'editor';
  const grupo = useGroupRef();
  const grupoDeDentro = useGroupRef();

  // Cada arranjo manda o seu layout uma vez. Dentro de um arranjo o arrasto
  // manda: o efeito só volta a falar quando a pessoa muda de tela ou abre o
  // primeiro arquivo.
  useEffect(() => {
    const { lateral, centro, direita } = LARGURAS[arranjo];
    grupo.current?.setLayout({ lateral, resto: 100 - lateral });
    grupoDeDentro.current?.setLayout({ centro, direita });
  }, [arranjo, grupo, grupoDeDentro]);

  function irPara(lado: LadoAberto): void {
    void navegar({ to: '/', search: escreverWorkspace({ ...estado, lado }) });
  }

  async function encerrar(): Promise<void> {
    await fetch(PREFIXO_API + ROTAS.auth.logout, { method: 'POST', credentials: 'include' });
    fecharSocketDoPainel();
    sair();
    void navegar({ to: '/entrar' });
  }

  const paginas = paginasVisiveis(usuario?.papel, eu?.recursos);
  const { mestres } = usarSessaoMestre();
  /*
    Com dois motores, a Prosa abre com o mestre PREFERIDO — e cai no primeiro
    quando a preferência ficou órfã. Escolher o motor toda vez seria um passo a
    mais no pior momento possível: quem abre a Prosa está com as mãos ocupadas.
  */
  const mestre = mestreDaProsa(mestres, lerMotorDaProsa());

  /**
   * Entra na Prosa sobre a conversa da mestre.
   *
   * `push`, nao `replace`: entrar e um passo, e o voltar do navegador tem de
   * sair dela -- do mesmo jeito que o botao de grade sai.
   */
  /**
   * Ctrl+. (ou Cmd+.) abre a Prosa.
   *
   * Ponto porque as teclas obvias ja tem dono: Ctrl+K e a barra de comandos,
   * Ctrl+P e o seletor de arquivo do editor, Ctrl+B recolhe painel. O ponto
   * esta livre em `Centro.tsx` e em `ListaDeSessoes.tsx`, que sao os outros
   * dois ouvintes de teclado do painel -- conferido, nao suposto.
   */
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent): void => {
      // `code` ANTES de `key`, e os dois aceitos.
      //
      // `key` e o caractere depois do layout: com Ctrl segurado, teclado ABNT2
      // e o do runner do CI nao entregam o mesmo valor, e o ensaio passava no
      // Windows e reprovava no Linux -- a armadilha que `agent-data/estado.md`
      // registra ("o portao roda no Windows; o CI roda no Linux"). `code` e a
      // POSICAO fisica da tecla e nao muda com layout nenhum.
      const ehPonto = e.code === 'Period' || e.code === 'NumpadDecimal' || e.key === '.';
      if (!ehPonto || !(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
      if (!mestre) return;
      e.preventDefault();
      irParaProsa();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  });

  function irParaProsa(): void {
    if (!mestre) return;
    void navegar({ to: '/', search: escreverWorkspace({ ...estado, prosa: true, sessao: mestre.id }) });
  }

  const [menuRecolhido, setMenuRecolhido] = useState(lerMenuRecolhido);
  function trocarMenu(): void {
    setMenuRecolhido((atual) => {
      gravarMenuRecolhido(!atual);
      return !atual;
    });
  }

  // Qual botão está aceso: no workspace é a view da lateral, nas outras telas
  // é a rota. Sem isso a barra fica apagada justamente onde a pessoa está.
  const caminho = useRouterState({ select: (e) => e.location.pathname });

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-zinc-950 font-sans text-zinc-100 antialiased">
      <div className="flex min-h-0 flex-1">
        {/* Menu lateral: para onde ir. */}
        <nav
          className={
            'flex shrink-0 flex-col border-r border-zinc-800 transition-[width] ' +
            (menuRecolhido ? 'w-12' : 'w-40')
          }
        >
          <div
            className={
              'flex items-center py-3 ' + (menuRecolhido ? 'justify-center px-1' : 'gap-1 px-2')
            }
          >
            <button
              type="button"
              onClick={() => trocarMenu()}
              aria-label={menuRecolhido ? 'Expandir o menu' : 'Recolher o menu'}
              title={menuRecolhido ? 'Expandir o menu' : 'Recolher o menu'}
              aria-expanded={!menuRecolhido}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-200"
            >
              <IconeMenu />
            </button>
            {menuRecolhido ? null : (
              <span className="truncate text-sm font-semibold tracking-tight text-zinc-100">
                Remoto
              </span>
            )}
          </div>

          {/*
            A porta da Prosa, acima de tudo e destacada.
 
            Ela nao entra em `PAGINAS` porque nao e uma rota: e um parametro
            (`prosa=1`) sobre a conversa da mestre, e precisa do id dela. E fica
            no TOPO, com cor propria, porque atalho que ninguem acha nao e
            atalho -- a primeira versao nao tinha botao nenhum e virou porta de
            mao unica: quem saia da Prosa nao tinha como voltar.
 
            Sem mestre no escopo, o botao nao aparece. Ausencia, nao botao cinza.
          */}
          {mestre ? (
            <div className={menuRecolhido ? 'px-1 pb-2' : 'px-2 pb-2'}>
              <button
                type="button"
                onClick={() => irParaProsa()}
                aria-label="Abrir a Prosa"
                title="Prosa — a conversa de voz com a mestre (Ctrl+.)"
                data-testid="ir-para-prosa"
                className={
                  'flex w-full items-center rounded-md bg-violet-600/90 py-2 text-left text-sm ' +
                  'font-medium text-white transition hover:bg-violet-500 ' +
                  (menuRecolhido ? 'justify-center px-0' : 'gap-2 px-2')
                }
              >
                <IconeMicrofone tamanho={16} />
                {menuRecolhido ? null : <span className="truncate">Prosa</span>}
              </button>
            </div>
          ) : null}

          <div className={'flex-1 space-y-0.5 ' + (menuRecolhido ? 'px-1' : 'px-2')}>
            {paginas.map((pagina) => {
              // As telas de dentro (o detalhe de uma rotina do Toolkit, de uma
              // gravação) acendem o item da tela de onde vieram.
              const aqui =
                caminho === pagina.rota || (pagina.rota !== '/' && caminho.startsWith(pagina.rota + '/'));
              return (
                <button
                  key={pagina.id}
                  onClick={() => void navegar({ to: pagina.rota })}
                  aria-current={aqui}
                  /**
                   * Recolhido, o nome sai da tela mas não do alcance de quem lê
                   * por leitor de tela nem de quem passa o mouse: ícone sem nome
                   * é enigma, e uma barra de enigmas é pior que uma barra larga.
                   */
                  aria-label={pagina.rotulo}
                  title={pagina.rotulo}
                  className={
                    'flex w-full items-center rounded-md py-1.5 text-left text-sm transition ' +
                    (menuRecolhido ? 'justify-center px-1' : 'gap-2.5 px-2.5') +
                    ' ' +
                    (aqui
                      ? 'bg-zinc-800 text-zinc-100'
                      : 'text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200')
                  }
                >
                  <pagina.Icone />
                  {menuRecolhido ? null : <span className="truncate">{pagina.rotulo}</span>}
                </button>
              );
            })}
          </div>

          {/*
            O tema fica no pé do menu, junto da conta: é ajuste que se mexe uma
            vez e não se procura de novo. "Sistema" é o padrão — quem usa o
            computador no claro não espera abrir uma tela preta.
          */}
          <div
            className={
              'pb-2 ' + (menuRecolhido ? 'flex flex-col items-center gap-1 px-1' : 'flex gap-1 px-2')
            }
          >
            {TEMAS.map((item) => (
              <button
                key={item.id}
                onClick={() => definirTema(item.id)}
                aria-current={escolhaDoTema === item.id}
                aria-label={item.titulo}
                title={item.titulo}
                className={
                  'flex items-center justify-center rounded py-1 transition ' +
                  (menuRecolhido ? 'h-7 w-7' : 'flex-1 px-1') +
                  ' ' +
                  (escolhaDoTema === item.id
                    ? 'bg-zinc-800 text-zinc-200'
                    : 'text-zinc-600 hover:bg-zinc-900 hover:text-zinc-300')
                }
              >
                <item.Icone tamanho={14} />
              </button>
            ))}
          </div>

          <div
            className={'border-t border-zinc-800 py-2 ' + (menuRecolhido ? 'px-1' : 'px-3')}
            title={menuRecolhido ? usuario?.nome : undefined}
          >
            {menuRecolhido ? null : (
              <p className="truncate text-xs text-zinc-500" title={usuario?.email}>
                {usuario?.nome}
              </p>
            )}
            <button
              onClick={() => void encerrar()}
              title="Sair"
              className={
                'text-xs text-zinc-600 hover:text-zinc-300 ' +
                (menuRecolhido ? 'w-full text-center' : 'mt-0.5')
              }
            >
              Sair
            </button>
          </div>
        </nav>

        <Group
          orientation="horizontal"
          className="flex min-w-0 flex-1"
          groupRef={grupo}
          // A lista de sessões rola, e a barra de rolagem dela cai exatamente
          // em cima da divisória: doze pixels de barra contra um de divisória,
          // e o ponteiro sempre pegava a barra. Aqui a área de arrasto passa a
          // ter tamanho de gente — mais larga ainda no toque.
          resizeTargetMinimumSize={{ coarse: 24, fine: 12 }}
        >
          <Panel
            key="lateral"
            id="lateral"
            collapsible
            collapsedSize="0"
            defaultSize="20"
            minSize="12"
            maxSize="40"
            className="min-w-0 overflow-hidden"
          >
            <aside className="flex h-full min-h-0 flex-col border-r border-zinc-800">
              {/* Abas da lateral: o que estou olhando enquanto trabalho. */}
              <div className="flex shrink-0 border-b border-zinc-800">
                {ABAS_DA_LATERAL.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => irPara(item.id)}
                    aria-current={estado.lado === item.id}
                    className={
                      'flex-1 border-b-2 px-2 py-2 text-xs transition ' +
                      (estado.lado === item.id
                        ? 'border-zinc-300 text-zinc-100'
                        : 'border-transparent text-zinc-500 hover:text-zinc-300')
                    }
                  >
                    {item.rotulo}
                  </button>
                ))}
              </div>
              <div className="min-h-0 flex-1">{lateral}</div>
            </aside>
          </Panel>

          <Divisoria oculta={LARGURAS[arranjo].lateral === 0} />

          {/*
            O editor e a conversa moram num grupo **dentro** do outro.
            Empilhados os três lado a lado, o centro ficava entre a lateral e a
            conversa: com ele em zero, arrastar a divisória fazia crescer um
            editor invisível em vez de trocar lateral por conversa. Aninhado, a
            divisória de fora sempre troca "lista" por "o resto", que é o que a
            mão espera; a de dentro troca editor por conversa.
          */}
          <Panel key="resto" id="resto" minSize="30" className="min-w-0 overflow-hidden">
            <Group
              orientation="horizontal"
              className="flex h-full min-w-0"
              groupRef={grupoDeDentro}
              resizeTargetMinimumSize={{ coarse: 24, fine: 12 }}
            >
              <Panel
                key="centro"
                id="centro"
                collapsible
                collapsedSize="0"
                defaultSize="65"
                className="min-w-0 overflow-hidden"
              >
                <main className="flex h-full min-h-0 flex-col">{centro}</main>
              </Panel>

              <Divisoria
                oculta={LARGURAS[arranjo].centro === 0 || LARGURAS[arranjo].direita === 0}
              />

              <Panel
                key="direita"
                id="direita"
                collapsible
                collapsedSize="0"
                defaultSize="35"
                className="min-w-0 overflow-hidden"
              >
                <aside className="flex h-full min-h-0 flex-col border-l border-zinc-800">
                  {direita}
                </aside>
              </Panel>
            </Group>
          </Panel>
        </Group>
      </div>

      {/* Barra de estado: o que precisa de gente fica visível de qualquer tela. */}
      <footer className="flex shrink-0 items-center gap-4 border-t border-zinc-800 bg-zinc-900/60 px-3 py-1 text-xs text-zinc-500">
        {pendentes.length > 0 ? (
          <button
            onClick={() => irPara('sessoes')}
            className="flex items-center gap-1.5 font-medium text-amber-300 hover:underline"
          >
            <IconeAviso tamanho={14} />
            {pendentes.length} permissão(ões) esperando resposta
          </button>
        ) : null}
        <div className="flex-1" />
        {maquinas.map((m) => (
          <span key={m.id} className="flex items-center gap-1.5" title={m.estado.toLowerCase()}>
            <span
              className={
                'h-1.5 w-1.5 rounded-full ' +
                (m.estado === 'ONLINE' ? 'bg-emerald-400' : 'bg-zinc-600')
              }
            />
            {m.nome}
          </span>
        ))}
      </footer>
    </div>
  );
}

/**
 * Divisória arrastável, larga o bastante para o ponteiro acertar.
 *
 * Continua montada quando o painel do lado está encolhido — a biblioteca exige
 * `Panel` e `Separator` como filhos diretos do `Group` —, mas some da vista e
 * para de arrastar: uma divisória solta na borda da tela só confunde.
 */
function Divisoria({ oculta = false }: { oculta?: boolean }): React.JSX.Element {
  return (
    <Separator
      disabled={oculta}
      className={
        'group relative shrink-0 outline-none ' +
        // `z-20` porque a divisória precisa ficar **acima** do painel vizinho:
        // sem isso a barra de rolagem da lista, que encosta nela, recebe o
        // clique primeiro e a divisória parece travada.
        (oculta ? 'w-0' : 'z-20 w-px cursor-col-resize bg-zinc-800')
      }
    >
      {oculta ? null : (
        <span className="absolute inset-y-0 -left-1.5 -right-1.5 transition group-hover:bg-zinc-500/60" />
      )}
    </Separator>
  );
}
